import { Component, OnInit, ElementRef, Renderer2, ViewChild, forwardRef, Input } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { GooglePlacesService } from '../../services/google-places/google-places.service';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

@Component({
  selector: 'app-address-fields',
  templateUrl: './address-fields.component.html',
  styleUrls: ['./address-fields.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AddressFieldsComponent),
      multi: true,
    },
  ],
})
export class AddressFieldsComponent implements OnInit, ControlValueAccessor {

  @Input() isRequired: boolean = false;

  value: any = {
    country: '',
    state: '',
    city: '',
    zipCode: '',
  };

  selectedCountry: any;
  statePredictions: google.maps.places.AutocompletePrediction[] = [];
  selectedStatePrediction: any;
  cityPredictions: google.maps.places.AutocompletePrediction[] = [];

  showStateResults: boolean = false;
  showCityResults: boolean = false;

  @ViewChild('stateInput') stateInput!: ElementRef;
  @ViewChild('cityInput') cityInput!: ElementRef;

  private onChange: (value: any) => void = () => {};
  private onTouched: () => void = () => {};

  private stateSearchSubject = new Subject<string>();
  private citySearchSubject = new Subject<string>();

  constructor(
    private _googlePlacesService: GooglePlacesService,
    private renderer: Renderer2
  ) {}

  ngOnInit() {
    this.setupOutsideClickHandler();

    this.stateSearchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(query => {
      if (query && query.length > 1) {
        this.fetchStatePredictions(query);
      } else {
        this.statePredictions = [];
      }
    });

    this.citySearchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(query => {
      if (query && query.length > 1) {
        this.fetchCityPredictions(query);
      } else {
        this.cityPredictions = [];
      }
    });
  }

  writeValue(value: any): void {
    if (value) {
      this.value = value;
    }
  }

  registerOnChange(fn: (value: any) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  onStateInput(event: any) {
    const query = event.target.value;
    this.stateSearchSubject.next(query);
  }

  onCityInput(event: any) {
    const query = event.target.value;
    this.citySearchSubject.next(query);
  }

  private fetchStatePredictions(query: any) {
    const componentRestrictions = this.selectedCountry?.code
      ? { country: this.selectedCountry.code.toLowerCase() }
      : null;

    this._googlePlacesService
      .getPlacePredictions(query, ['(regions)'], componentRestrictions)
      .then((predictions: google.maps.places.AutocompletePrediction[]) => {
        this.statePredictions = predictions.filter((result) => this.isState(result));
        this.showStateResults = true;
      })
      .catch(() => {
        this.statePredictions = [];
      });
  }

  private fetchCityPredictions(query: any) {
    const componentRestrictions = this.selectedCountry?.code
      ? { country: this.selectedCountry.code.toLowerCase() }
      : null;

    this._googlePlacesService
      .getPlacePredictions(query, ['(cities)'], componentRestrictions)
      .then((predictions: google.maps.places.AutocompletePrediction[]) => {
        this.cityPredictions = predictions.filter((prediction) => {
          return this.selectedStatePrediction
            ? prediction.description.includes(this.selectedStatePrediction.terms[0].value)
            : true;
        });

        this.showCityResults = true;
      })
      .catch(() => {
        this.cityPredictions = [];
      });
  }

  selectState(prediction: google.maps.places.AutocompletePrediction) {
    this.value.state = prediction.terms[0].value;
    this.selectedStatePrediction = prediction;
    this.statePredictions = [];
    this.showStateResults = false;
    this.onChange(this.value);
  }

  selectCity(prediction: google.maps.places.AutocompletePrediction) {
    this.value.city = prediction.terms[0].value;
    this.cityPredictions = [];
    this.showCityResults = false;
    this.onChange(this.value);
  }

  selectCountry(country: any) {
    this.selectedCountry = country;
    this.value.country = country;

    this.value.state = '';
    this.value.city = '';
    this.value.zipCode = '';
    this.selectedStatePrediction = null;
    this.statePredictions = [];
    this.cityPredictions = [];

    this.onChange(this.value);
  }

  private isState(result: any): boolean {
    return result.types.includes('administrative_area_level_1');
  }

  setupOutsideClickHandler() {
    this.renderer.listen('document', 'click', (event) => {
      if (
        !this.stateInput?.nativeElement.contains(event.target) &&
        !this.cityInput?.nativeElement.contains(event.target)
      ) {
        this.showStateResults = false;
        this.showCityResults = false;
      }
    });
  }
}